import { FC, useEffect, useMemo, useRef } from 'react'

import { ObjectDataRecord } from '@types'

import { getPaperDimensions } from '../lib/getPaperDimensions'
import { postprocessCodes } from '../lib/postprocessCodes'
import { createPrintDocumentRenderer } from '../lib/renderPrintDocument'
import { PrintFormConfig } from '../types'

import { PrintRoot } from './PrintRoot'

import '../styles/print.css'

type PrintTriggerProps = {
  config: PrintFormConfig
  records: ObjectDataRecord[]
  onDone: () => void
}

export const PrintTrigger: FC<PrintTriggerProps> = ({ config, records, onDone }) => {
  const printRootRef = useRef<HTMLDivElement | null>(null)

  const dimensions = useMemo(
    () => getPaperDimensions(config.layout.paperSize, config.layout.orientation),
    [config.layout]
  )

  const pageCss = `@page{margin:0;size:${dimensions.width}mm ${dimensions.height}mm;}`

  const renderDocument = useMemo(
    () => createPrintDocumentRenderer(config.documentTemplate, config.documentPartials ?? {}),
    [config.documentTemplate, config.documentPartials]
  )

  const safeHtml = useMemo(
    () =>
      renderDocument({
        records,
        paperMm: dimensions,
        templateContext: config.templateContext,
      }),
    [config.templateContext, dimensions, records, renderDocument]
  )

  useEffect(() => {
    const printRoot = printRootRef.current
    if (!printRoot) return

    let isDone = false
    let cleanupFallbackId: ReturnType<typeof setTimeout> | null = null

    const finish = () => {
      if (isDone) return
      isDone = true
      if (cleanupFallbackId) {
        clearTimeout(cleanupFallbackId)
      }
      window.removeEventListener('afterprint', finish)
      onDone()
    }

    postprocessCodes(printRoot, records)
      .catch(() => undefined)
      .finally(() => {
        window.addEventListener('afterprint', finish, { once: true })
        window.print()
        cleanupFallbackId = setTimeout(finish, 3000)
      })

    return () => {
      if (cleanupFallbackId) clearTimeout(cleanupFallbackId)
      window.removeEventListener('afterprint', finish)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PrintRoot>
      <style dangerouslySetInnerHTML={{ __html: pageCss }} />
      <div ref={printRootRef} dangerouslySetInnerHTML={{ __html: safeHtml }} />
    </PrintRoot>
  )
}
